import * as React from 'react';
import {
    AddedArticleIcon,
    addIcon,
    discountIcon,
    hiddenArticleIcon,
} from '@europrocurement/l2d-icons';
import { Box, Card, Palette, PaletteColor, Typography } from '@mui/material';
import { SortableItem, SortableList } from '@europrocurement/l2d-modules/modules/Offres';
import {
    ArticleTarifDTOJsonldDonneesFacturationRead,
    DossierTarifDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import {
    Accordion,
    FlexyButton,
    ListCardBone,
    SkeletonLoader,
    Switch,
    useModal,
} from '@europrocurement/flexy-components';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DossiersArticlesReductions } from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierPrefacturation';
import ArticlePrefacturationHeader from '../../../ArticlePrefacturationHeader/ArticlePrefacturationHeader';
import ArticlePrefacturation from '../../../ArticlePrefacturation/ArticlePrefacturation';
import NoResult from '../../../NoResult/NoResult';
import ArticleAdd from '../../../ArticleAdd/ArticleAdd';

type OrderDetailsSectionProps = {
    isTech: boolean;
    remisesInvisiblesAffichees: boolean;
    setRemisesInvisiblesAffichees: React.Dispatch<React.SetStateAction<boolean>>;
    collection: CollectionDeDossierJsonldCollectiondossierRead;
    dataPrefacturation: DossiersArticlesReductions;
    dataPrefacturationDossiers: DossierTarifDTOJsonldDonneesFacturationRead[];
    palette: Palette;
    afterSubmit: () => void;
    sortableListOnChange: (
        items: (Omit<ArticleTarifDTOJsonldDonneesFacturationRead, 'id'> & {
            id: number;
        })[],
        dossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => void;
    onChangeDossiersSelected?: (id: number, checked: boolean) => void;
    dossiersSelected?: Array<number>;
};

const OrderDetailsSection: React.FunctionComponent<OrderDetailsSectionProps> = function ({
    isTech,
    remisesInvisiblesAffichees,
    setRemisesInvisiblesAffichees,
    collection,
    dataPrefacturation,
    dataPrefacturationDossiers,
    palette,
    afterSubmit,
    sortableListOnChange,
    onChangeDossiersSelected,
    dossiersSelected,
}: OrderDetailsSectionProps) {
    const { modalActions } = useModal();

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: 'none',
                padding: 0,
            }}
            id="DetailsDeLaCommande"
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Détails de la commande"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        {isTech ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    variant="l2dh7"
                                    sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                                >
                                    Afficher articles cachés
                                </Typography>
                                <Switch
                                    value={remisesInvisiblesAffichees}
                                    checked={remisesInvisiblesAffichees}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                                    }}
                                    onChange={(e, value) => {
                                        setRemisesInvisiblesAffichees(value);
                                    }}
                                />
                            </Box>
                        ) : null}
                    </Box>
                }
            >
                <SkeletonLoader
                    isLoading={!(collection && dataPrefacturationDossiers)}
                    skeleton={<ListCardBone />}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxShadow: 'none',
                            padding: 0,
                        }}
                    >
                        {collection &&
                            dataPrefacturationDossiers.map((dossier, dossierIdx) => (
                                <Box>
                                    <Accordion
                                        sx={{
                                            boxShadow: 'none',
                                        }}
                                        title={
                                            dossier.prestations
                                                ?.map((prestation) => prestation.libelle)
                                                .join(' - ') || ''
                                        }
                                        titlesSx={{
                                            fontWeight: '600',
                                        }}
                                        subtitle={`Ref. Dossier #${dossier.numero}`}
                                        expanded
                                        expandColor={
                                            palette.text.primary as unknown as PaletteColor
                                        }
                                        expandLabel={
                                            <>
                                                <Typography
                                                    variant="l2dlabel"
                                                    sx={{ color: 'text.gris70' }}
                                                >
                                                    Total TTC :
                                                </Typography>
                                                <Typography
                                                    variant="l2dh7"
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontWeight: '600',
                                                    }}
                                                >{` ${dossier.ttc} €`}</Typography>
                                            </>
                                        }
                                        footerActions={
                                            dossier.prestations && [
                                                <FlexyButton
                                                    variant="outlined"
                                                    color={palette.primary.dark}
                                                    onClick={() => {
                                                        if (!dossier.prestations) {
                                                            throw new Error(
                                                                'dossier.prestations is undefined',
                                                            );
                                                        }
                                                        if (!dataPrefacturation.tauxTvas) {
                                                            throw new Error(
                                                                'dataPrefacturation.tauxTvas is undefined',
                                                            );
                                                        }

                                                        modalActions.call(
                                                            <ArticleAdd
                                                                tauxTva={
                                                                    dataPrefacturation.tauxTvas
                                                                }
                                                                prestations={dossier.prestations}
                                                                afterSubmit={afterSubmit}
                                                            />,
                                                        );
                                                    }}
                                                    icon={addIcon}
                                                    label="Ajouter un article"
                                                />,
                                            ]
                                        }
                                        checkBox={(dataPrefacturation.dossiers?.length ?? 0) > 1}
                                        checkBoxDisabled={!dossier.dossierFacturable}
                                        checked={
                                            dossier.id !== undefined &&
                                            dossiersSelected &&
                                            dossiersSelected.includes(dossier.id)
                                        }
                                        onChange={(checked) => {
                                            console.log(checked);
                                            console.log(dossier.id);
                                            if (
                                                dossier.id !== undefined &&
                                                onChangeDossiersSelected
                                            ) {
                                                console.log('onChangeDossiersSelected');
                                                onChangeDossiersSelected(dossier.id, checked);
                                            }
                                            console.log('apres onChangeDossiersSelected');
                                        }}
                                    >
                                        <ArticlePrefacturationHeader />

                                        {dossier.articles ? (
                                            <SortableList
                                                style={{ margin: '0px' }}
                                                items={
                                                    dossier.articles.filter(
                                                        (art) =>
                                                            art.id !== undefined &&
                                                            !art.fMasqueCalcul,
                                                    ) as Array<
                                                        Omit<
                                                            ArticleTarifDTOJsonldDonneesFacturationRead,
                                                            'id'
                                                        > & {
                                                            id: number;
                                                        }
                                                    >
                                                }
                                                onChange={(items) => {
                                                    sortableListOnChange(
                                                        items,
                                                        dossier,
                                                        dossierIdx,
                                                    );
                                                }}
                                                renderItem={(article) => {
                                                    const key = `facturation_collection_article_${article.id}`;
                                                    return (
                                                        <SortableItem
                                                            id={article.id}
                                                            key={key}
                                                        >
                                                            <ArticlePrefacturation
                                                                article={article}
                                                                afterSubmit={afterSubmit}
                                                                sortabled
                                                                icon={
                                                                    remisesInvisiblesAffichees &&
                                                                    article.fAjouteCalcul
                                                                        ? AddedArticleIcon
                                                                        : null
                                                                }
                                                            />
                                                        </SortableItem>
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <NoResult
                                                title="Aucun article pour le moment"
                                                paddingSx="15px"
                                            />
                                        )}
                                        {dossier.articles && remisesInvisiblesAffichees
                                            ? dossier.articles
                                                  .filter(
                                                      (art) =>
                                                          art.id !== undefined && art.fMasqueCalcul,
                                                  )
                                                  .map((article) => (
                                                      <ArticlePrefacturation
                                                          article={article}
                                                          sx={{ marginTop: '10px' }}
                                                          icon={hiddenArticleIcon}
                                                      />
                                                  ))
                                            : null}
                                        {dossier.reductions && remisesInvisiblesAffichees
                                            ? dossier.reductions.map((reduction) => {
                                                  const articleReduction: Omit<
                                                      ArticleTarifDTOJsonldDonneesFacturationRead,
                                                      'id'
                                                  > & { id: number } = {
                                                      ...reduction,
                                                      id: Math.random(),
                                                      idAnnonce:
                                                          reduction.idAnnonce !== undefined
                                                              ? String(reduction.idAnnonce)
                                                              : undefined,
                                                  };
                                                  return (
                                                      <ArticlePrefacturation
                                                          article={articleReduction}
                                                          sx={{ marginTop: '10px' }}
                                                          icon={discountIcon}
                                                      />
                                                  );
                                              })
                                            : null}
                                    </Accordion>
                                </Box>
                            ))}
                    </Box>
                </SkeletonLoader>
            </Accordion>
        </Card>
    );
};

export default OrderDetailsSection;
